/**
 * WeatherPanel — drone briefing weather card.
 *
 * Consumes the GET /api/weather payload built by backend/weather_service.py
 * and renders a dark, dense, drone-pilot-oriented layout inspired by the
 * "DroneWeather" reference shared by the product owner. Each metric is
 * color-flagged (green/amber/red) by the backend using the CE class
 * thresholds, so the UI just renders the dots and labels.
 *
 * Props:
 *   - lat, lon (required) — center of the mission polygon
 *   - date (YYYY-MM-DD), time (HH:MM)
 *   - ceClass — drone CE class ("C0".."C4")
 *   - droneName — optional pretty label shown in the "PROFIL ACTIF" pill
 *   - snapshot — optional pre-computed payload (used on MissionDetailPage
 *     to show the persisted briefing without an extra HTTP call).
 */

import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  AlertOctagon, ShieldCheck, ShieldAlert, Wind, Thermometer, Droplets,
  Cloud, Gauge, Eye, Sun, Sunrise, Sunset, Compass, Zap, Snowflake,
  CloudRain, Activity, Loader2, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Color tokens — kept in one place so the whole panel stays consistent and
// future restyling is a one-line change.
// ---------------------------------------------------------------------------
const FLAG_DOT = {
  green:   'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]',
  amber:   'bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.6)]',
  red:     'bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.7)]',
  unknown: 'bg-slate-500',
};

const VERDICT = {
  FAVORABLE: { label: 'FAVORABLE',     cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', Icon: ShieldCheck },
  MARGINAL:  { label: 'MARGINAL',      cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40',       Icon: ShieldAlert },
  NON_SUR:   { label: 'NON SÛR',       cls: 'bg-rose-500/15 text-rose-300 border-rose-500/40',          Icon: AlertOctagon },
};

const fmt = (v, digits = 1) =>
  v === null || v === undefined || Number.isNaN(v) ? 'N/D' : Number(v).toFixed(digits);

// ---------------------------------------------------------------------------
// Sub-components (intentionally tiny — keep WeatherPanel skimmable)
// ---------------------------------------------------------------------------
const Dot = ({ flag }) => (
  <span className={`inline-block w-2 h-2 rounded-full ${FLAG_DOT[flag] || FLAG_DOT.unknown}`} />
);

const MetricCell = ({ icon: Icon, label, value, unit, flag, sub, testid }) => (
  <div
    className="flex items-start gap-2.5 rounded-lg bg-slate-900/60 border border-slate-800/80 px-3 py-2.5"
    data-testid={testid}
  >
    {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />}
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium flex items-center gap-1.5">
        {label}
        <Dot flag={flag} />
      </div>
      <div className="text-sm font-semibold text-slate-100 tabular-nums">
        {value}{unit ? <span className="text-slate-400 ml-1 font-normal">{unit}</span> : null}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  </div>
);

const HeroCurrent = ({ payload }) => {
  const c = payload?.current || {};
  const temp = c.temperature?.value;
  const feel = c.apparent_temperature?.value;
  const wind = c.wind_10m?.value;
  const gusts = c.wind_gusts?.value;
  return (
    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 mb-4">
      <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2">Météo actuelle</div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Thermometer className="w-7 h-7 text-sky-400" />
          <div>
            <div className="text-3xl font-bold text-slate-50 tabular-nums leading-none">
              {fmt(temp)} <span className="text-base text-slate-400 font-normal">°C</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">Ressenti {fmt(feel)} °C</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Wind className="w-7 h-7 text-sky-400" />
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end">
              <span className="text-3xl font-bold text-slate-50 tabular-nums leading-none">
                {fmt(wind)} <span className="text-base text-slate-400 font-normal">km/h</span>
              </span>
              <Dot flag={c.wind_10m?.flag} />
            </div>
            <div className="text-xs text-slate-400 mt-1 flex items-center gap-1.5 justify-end">
              Rafales {fmt(gusts)} km/h <Dot flag={c.wind_gusts?.flag} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function WeatherPanel({
  lat, lon, date, time = '10:00', ceClass, droneName, snapshot, onChangeDrone,
  compact = false,
}) {
  const [data, setData] = useState(snapshot || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canFetch = useMemo(
    () => Number.isFinite(lat) && Number.isFinite(lon) && !!date,
    [lat, lon, date],
  );

  const fetchWeather = async () => {
    if (!canFetch) return;
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await axios.get(`${API}/api/weather`, {
        params: { lat, lon, date, time, ce_class: ceClass || undefined },
      });
      setData(d);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur météo';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch whenever the location/date/time/CE class change. Snapshot, if
  // present at first render, is reused as the initial state but the effect
  // still refreshes it so users get live data when revisiting an old mission.
  // The fetchWeather helper is defined inside this component, so eslint
  // wants it in the deps array. We deliberately omit it: adding it would
  // create a new function reference on every render and cause the
  // Open-Meteo request to fire infinitely. The primitive deps we DO
  // list are sufficient — `canFetch` is derived from them via useMemo
  // and stable across identical inputs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!canFetch) return;
    fetchWeather();
  }, [lat, lon, date, time, ceClass]);

  if (!canFetch) {
    return (
      <div className="rounded-xl bg-slate-950 border border-slate-800 p-6 text-center text-sm text-slate-400">
        Sélectionnez une zone de vol et une date pour afficher la météo.
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="rounded-xl bg-slate-950 border border-slate-800 p-6 flex items-center justify-center text-slate-400 text-sm gap-2" data-testid="weather-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Récupération du briefing météo…
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-xl bg-slate-950 border border-rose-800/40 p-6 text-sm text-rose-300" data-testid="weather-error">
        <div className="flex items-center gap-2 mb-2">
          <AlertOctagon className="w-4 h-4" /> Service météo indisponible
        </div>
        <div className="text-xs text-slate-400">{error}</div>
        <button onClick={fetchWeather} className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs">
          <RefreshCw className="w-3 h-3" /> Réessayer
        </button>
      </div>
    );
  }

  const verdictDef = VERDICT[data?.verdict] || VERDICT.MARGINAL;
  const VIcon = verdictDef.Icon;
  const c = data?.current || {};

  return (
    <div className="rounded-xl bg-gradient-to-b from-slate-950 to-slate-900 border border-slate-800 p-4 sm:p-5 text-slate-200" data-testid="weather-panel">
      {/* Verdict banner */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5">Sécurité de vol</div>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border ${verdictDef.cls}`} data-testid="weather-verdict">
            <VIcon className="w-4 h-4" />
            {verdictDef.label}
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Profil actif : <span className="font-mono text-slate-300">{droneName || ceClass || 'Standard C2'}</span>
            {onChangeDrone && (
              <button onClick={onChangeDrone} className="ml-2 text-sky-400 hover:text-sky-300 underline-offset-2 hover:underline" data-testid="weather-change-drone">
                changer de drone
              </button>
            )}
          </div>
        </div>
        <button
          onClick={fetchWeather}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-slate-800/80 hover:bg-slate-700 text-slate-300 text-xs disabled:opacity-50"
          data-testid="weather-refresh"
          title="Rafraîchir"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </button>
      </div>

      <HeroCurrent payload={data} />

      {/* Wind altitudes */}
      <div className="grid grid-cols-2 gap-2.5 mb-2.5">
        <MetricCell icon={Wind}  label="Vent 80 m"  value={fmt(c.wind_80m?.value)}  unit="km/h" flag={c.wind_80m?.flag}  testid="weather-wind-80" />
        <MetricCell icon={Wind}  label="Vent 120 m" value={fmt(c.wind_120m?.value)} unit="km/h" flag={c.wind_120m?.flag} testid="weather-wind-120" />
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-2.5">
        <MetricCell
          icon={Compass}
          label="Direction"
          value={`${c.wind_direction?.cardinal || '—'} (${fmt(c.wind_direction?.value, 0)}°)`}
          flag={c.wind_direction?.flag}
          testid="weather-direction"
        />
        <MetricCell
          icon={CloudRain}
          label="Précipitations"
          value={fmt(c.precipitation?.value, 1)}
          unit="mm"
          flag={c.precipitation?.flag}
          sub={c.precipitation?.probability !== undefined ? `Probabilité ${c.precipitation?.probability}%` : null}
          testid="weather-precip"
        />
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-2.5">
        <MetricCell
          icon={Cloud}
          label="Nuages"
          value={`${fmt(c.cloud_cover?.value, 0)}%`}
          flag={c.cloud_low?.flag}
          sub={`Bas : ${fmt(c.cloud_low?.value, 0)}%`}
          testid="weather-cloud"
        />
        <MetricCell
          icon={Gauge}
          label="Pression"
          value={fmt(c.pressure_msl?.value, 0)}
          unit="hPa"
          flag={c.pressure_msl?.flag}
          sub={`Surface ${fmt(c.pressure_surface?.value, 0)} hPa`}
          testid="weather-pressure"
        />
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-2.5">
        <MetricCell icon={Activity} label="Indice Kp" value={fmt(c.kp_index?.value, 1)} flag={c.kp_index?.flag} sub="GPS / boussole" testid="weather-kp" />
        <MetricCell icon={Zap}      label="CAPE"      value={fmt(c.cape?.value, 0)}     unit="J/kg" flag={c.cape?.flag} sub="risque orage" testid="weather-cape" />
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-2.5">
        <MetricCell icon={Thermometer} label="Temp. 80 m"  value={fmt(c.temperature_80m?.value)}  unit="°C" flag={c.temperature_80m?.flag} testid="weather-temp-80" />
        <MetricCell icon={Thermometer} label="Temp. 120 m" value={fmt(c.temperature_120m?.value)} unit="°C" flag={c.temperature_120m?.flag} testid="weather-temp-120" />
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-2.5">
        <MetricCell icon={Eye}     label="Visibilité" value={fmt(c.visibility_km?.value, 1)} unit="km" flag={c.visibility_km?.flag} testid="weather-visi" />
        <MetricCell icon={Droplets} label="Humidité"  value={fmt(c.humidity?.value, 0)} unit="%" flag={c.humidity?.flag} testid="weather-humidity" />
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <MetricCell icon={Sunrise} label="Lever du soleil"   value={(data?.sun?.sunrise || '').slice(11, 16) || '—'} flag="green" testid="weather-sunrise" />
        <MetricCell icon={Sunset}  label="Coucher du soleil" value={(data?.sun?.sunset || '').slice(11, 16)  || '—'} flag="green" testid="weather-sunset" />
      </div>

      {/* Propeller icing — separate single row to mirror the reference UI */}
      <div className="mt-3">
        <MetricCell
          icon={Snowflake}
          label="Givrage hélice"
          value={
            c.propeller_icing?.flag === 'green' ? 'FAVORABLE'
              : c.propeller_icing?.flag === 'amber' ? 'ATTENTION'
              : c.propeller_icing?.flag === 'red' ? 'DANGER'
              : 'N/D'
          }
          flag={c.propeller_icing?.flag}
          sub="Risque calculé température + humidité"
          testid="weather-icing"
        />
      </div>

      <div className="mt-4 text-[11px] text-slate-500 flex items-center justify-between">
        <span>Source : Open-Meteo + NOAA SWPC</span>
        {data?.at && <span>Heure analysée : {data.at.slice(11, 16)}</span>}
      </div>

      {compact && null}
    </div>
  );
}
